import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { updateContractorCard } from "@/services/contractorCards";
import {
  ReadContractorCard,
  UpdateContractorCard,
} from "@/types/contractorCard";
import { ApiError } from "@/services/api";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { Module } from "@/constants";
import { useFilePreview } from "@/components/file-preview/hooks";
import { buildFileUrl } from "@/lib/utils/buildFileUrl";

export const useContractorCardHandlers = (
  contractorCardId: string,
  contractorCard?: ReadContractorCard,
) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { preview, openPreviewDialog, closePreview } = useFilePreview({
    onError: () => {
      toast({
        title: t("contractorCards.errors.noFileToView"),
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutationWithErrorHandling<
    ReadContractorCard,
    ApiError,
    UpdateContractorCard,
    unknown
  >({
    mutationFn: (data) => updateContractorCard(contractorCardId, data),
    errorTitle: t("contractorCards.errors.update"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [Module.ContractorCards] });
      toast({ title: t("contractorCards.success.update") });
    },
  });

  const handleUpdate = useCallback(
    (data: UpdateContractorCard) => updateMutation.mutate(data),
    [updateMutation],
  );

  const handleFileUpload = useCallback(
    async (content: string, filename: string) => {
      await updateMutation.mutateAsync({
        content,
        filename,
      });
    },
    [updateMutation],
  );

  const handleView = useCallback(() => {
    if (!contractorCard?.card_s3_key || !contractorCard?.card_filename) {
      toast({
        title: t("contractorCards.errors.noFileToView"),
        variant: "destructive",
      });
      return;
    }
    const fileUrl = buildFileUrl(contractorCard.card_s3_key);
    if (fileUrl) {
      openPreviewDialog(fileUrl, contractorCard.card_filename);
    }
  }, [contractorCard, openPreviewDialog, t, toast]);

  return {
    handleUpdate,
    handleFileUpload,
    handleView,
    mutations: { updateContractorCardMutation: updateMutation },
    previewDialog: preview,
    handleClosePreview: closePreview,
  };
};
